import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { PHONE_DISPLAY, PHONE_TEL } from "@/components/shared";

const btnGold = "block text-center px-6 py-4 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-xs hover:opacity-95 transition-opacity shadow-lg shadow-[#d4af37]/20";
const btnCall = "block text-center px-6 py-4 rounded border-2 border-[#d4af37] text-[#d4af37] font-bold uppercase tracking-wider text-xs hover:bg-[#d4af37] hover:text-black transition-colors";

const CONCEALMENT_DISCLOSURE = "Standard in-wall wire concealment is included. Additional charges of up to $350 may apply for custom wire routing, outlet location issues, or wall obstructions. Additional costs are determined on-site and require customer approval.";

const PROMO3_SIZES = [
  { size: "tv-51-64", label: "Up to 60 inches", price: 160 },
  { size: "tv-65-75", label: "61–75 inches", price: 199 },
  { size: "tv-76-85", label: "76–110 inches", price: 499 },
];

function PromoShell({ id, testid, price, title, items, note, children }) {
  return (
    <section id={id} data-testid={testid}
      className="rounded-2xl bg-[#121216] gold-border p-6 sm:p-8 scroll-mt-24 flex flex-col">
      <p className="font-display text-5xl text-[#d4af37]">${price}</p>
      <h2 className="font-display text-xl uppercase text-white mt-2 leading-tight">{title}</h2>
      <ul className="mt-5 space-y-2 flex-1">
        {items.map((i) => (
          <li key={i} className="text-[12px] text-zinc-300 leading-relaxed flex gap-2">
            <span className="text-[#d4af37] mt-0.5">•</span>{i}
          </li>
        ))}
      </ul>
      {note && <p className="text-[10px] font-mono text-zinc-500 leading-relaxed mt-4 border-t border-zinc-800 pt-4">{note}</p>}
      {children}
    </section>
  );
}

const AREAS_INTRO = "DuroTech28 is based in Irving, Texas — including Las Colinas — and our installers cover the entire Dallas–Fort Worth Metroplex. Whether it's TV mounting in Irving, a smart doorbell in Coppell, or a home theater in Dallas, the same professional standard applies at every address.";
const AREAS_OTHER = ["Dallas", "Grand Prairie", "Arlington", "Coppell", "Carrollton", "Farmers Branch",
  "Grapevine", "Richardson", "Plano", "Frisco", "Allen", "McKinney", "Garland", "Euless",
  "Bedford", "Hurst", "Fort Worth"];

const jsonLd = [
  {
    "@context": "https://schema.org", "@type": "OfferCatalog",
    "name": "DuroTech28 Installation Promotions",
    "url": "https://durotech28.me/promotions",
    "provider": { "@type": "LocalBusiness", "name": "DuroTech28", "telephone": "+1-469-897-6497", "url": "https://durotech28.me" },
    "itemListElement": [
      { "@type": "Offer", "name": "Smart Home Upgrade Bundle — TV mounting, video doorbell and smart thermostat installation", "price": "450", "priceCurrency": "USD", "areaServed": "Dallas–Fort Worth, TX" },
      { "@type": "Offer", "name": "TV + Electric Fireplace Bundle — TV mounting and electric fireplace installation", "price": "275", "priceCurrency": "USD", "areaServed": "Dallas–Fort Worth, TX" },
      { "@type": "Offer", "name": "TV Mounting + In-Wall Wire Concealment (up to 60 in)", "price": "160", "priceCurrency": "USD", "areaServed": "Dallas–Fort Worth, TX" },
      { "@type": "Offer", "name": "TV Mounting + In-Wall Wire Concealment (61–75 in)", "price": "199", "priceCurrency": "USD", "areaServed": "Dallas–Fort Worth, TX" },
      { "@type": "Offer", "name": "TV Mounting + In-Wall Wire Concealment (76–110 in)", "price": "499", "priceCurrency": "USD", "areaServed": "Dallas–Fort Worth, TX" },
      { "@type": "Offer", "name": "Premium Home Theater Installation (starting price)", "priceSpecification": { "@type": "PriceSpecification", "minPrice": "1500", "priceCurrency": "USD" }, "areaServed": "Dallas–Fort Worth, TX" },
      { "@type": "Offer", "name": "Luxury Modular Media Wall Installation (starting price)", "priceSpecification": { "@type": "PriceSpecification", "minPrice": "799", "priceCurrency": "USD" }, "areaServed": "Dallas–Fort Worth, TX" },
    ],
  },
];

export default function PromotionsPage() {
  return (
    <>
      <Seo
        title="Installation Deals in Irving & Dallas–Fort Worth | DuroTech28"
        description="TV mounting, smart home and home theater installation deals in Irving, Las Colinas and Dallas–Fort Worth. Pay now or after installation. Call 469-897-6497."
        canonical="https://durotech28.me/promotions"
        jsonLd={jsonLd}
      />
      <Nav />
      <main data-testid="promotions-page">
        <header className="border-b border-[#d4af37]/15">
          <div className="max-w-4xl mx-auto px-6 pt-16 pb-12 text-center">
            <span className="text-xs uppercase tracking-widest text-[#d4af37] font-mono">Limited Availability</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white mt-4 leading-[1.02]">
              Exclusive DuroTech28 Installation Deals
            </h1>
            <p className="text-sm sm:text-base text-zinc-400 max-w-xl mx-auto leading-relaxed mt-5">
              Current installation specials across Irving and Dallas–Fort Worth — TV mounting with hidden
              wires, smart home installs, home theater and media walls. Book online in about two minutes —
              pay now or pay after installation, your choice.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <a href={PHONE_TEL} data-testid="promo-call-btn" className={btnCall}>Call {PHONE_DISPLAY}</a>
              <a href="#tv-concealment" data-testid="promo-jump-deals" className={btnGold}>See The Deals ↓</a>
            </div>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-3 gap-6">
          <PromoShell id="smart-home-bundle" testid="promo-smart-home" price={450}
            title="Smart Home Upgrade Bundle"
            items={[
              "Professional TV mounting and basic setup",
              "Smart video doorbell installation (Ring and other major brands)",
              "Smart thermostat installation for Dallas–Fort Worth homes",
              "FREE compatible standard flat TV mount",
            ]}
            note="TV and smart devices sold separately. Specialty mounting, electrical modifications, and additional installation requirements may incur extra charges.">
            <Link to="/book?promo=smart-home-bundle" data-testid="book-smart-home-btn" className={`${btnGold} mt-6`}>
              Book $450 Bundle
            </Link>
          </PromoShell>

          <PromoShell id="fireplace-bundle" testid="promo-fireplace" price={275}
            title="TV + Electric Fireplace Bundle"
            items={[
              "Professional TV mounting",
              "Electric fireplace installation",
              "FREE compatible standard flat TV mount",
            ]}
            note="Customer supplies the TV and fireplace. Electrical modifications, recessed power installation, and specialty wall construction are not included unless separately quoted.">
            <Link to="/book?promo=fireplace-bundle" data-testid="book-fireplace-btn" className={`${btnGold} mt-6`}>
              Book $275 Bundle
            </Link>
          </PromoShell>

          <section id="tv-concealment" data-testid="promo-tv-concealment"
            className="rounded-2xl bg-[#121216] gold-border p-6 sm:p-8 scroll-mt-24 flex flex-col">
            <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">Free standard flat mount included</p>
            <h2 className="font-display text-xl uppercase text-white mt-2 leading-tight">TV Mounting + In-Wall Wire Concealment</h2>
            <ul className="mt-4 space-y-1.5 text-[12px] text-zinc-300 leading-relaxed">
              {["Professional TV mounting", "FREE compatible standard flat TV bracket",
                "Professional leveling and secure installation",
                "In-wall wire concealment within the same unobstructed stud bay",
                "Basic TV setup"].map((i) => (
                <li key={i} className="flex gap-2"><span className="text-[#d4af37] mt-0.5">•</span>{i}</li>
              ))}
            </ul>
            <div className="mt-5 space-y-2 flex-1">
              {PROMO3_SIZES.map((s) => (
                <Link key={s.size} to={`/book?promo=tv-concealment&size=${s.size}`}
                  data-testid={`book-concealment-${s.size}`}
                  className="flex items-center justify-between rounded-lg border border-zinc-700 px-4 py-3.5 hover:border-[#d4af37] transition-colors group">
                  <span className="text-xs font-mono text-zinc-300 group-hover:text-white">{s.label}</span>
                  <span className="font-display text-xl text-[#d4af37]">${s.price}</span>
                </Link>
              ))}
            </div>
            <p className="text-[10px] font-mono text-zinc-500 leading-relaxed mt-4 border-t border-zinc-800 pt-4">
              {CONCEALMENT_DISCLOSURE} In-wall power uses code-compliant methods — standard TV power cords are never
              routed inside walls; any separately priced electrical work is quoted and approved first. Specialty walls,
              unusual mounting conditions, additional technicians, or special materials may be quoted separately.
            </p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-4 text-center">Select your TV size above to book</p>
          </section>
        </div>

        {/* Starting-at promos */}
        <div className="max-w-6xl mx-auto px-6 pb-14 grid md:grid-cols-2 gap-6">
          <section id="home-theater" data-testid="promo-home-theater"
            className="rounded-2xl bg-[#121216] gold-border p-6 sm:p-8 scroll-mt-24 flex flex-col">
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Starting at</p>
            <p className="font-display text-5xl text-[#d4af37]">$1,500</p>
            <h2 className="font-display text-xl uppercase text-white mt-2 leading-tight">Premium Home Theater Installation</h2>
            <ul className="mt-5 space-y-2 flex-1">
              {["Surround sound system installation for Dallas–Fort Worth homes", "TV or projector mounting", "Speaker installation and placement",
                "AV receiver setup", "Full system configuration and walkthrough"].map((i) => (
                <li key={i} className="text-[12px] text-zinc-300 leading-relaxed flex gap-2">
                  <span className="text-[#d4af37] mt-0.5">•</span>{i}
                </li>
              ))}
            </ul>
            <p className="text-[10px] font-mono text-zinc-500 leading-relaxed mt-4 border-t border-zinc-800 pt-4">
              Starting price covers qualifying installation labor. Equipment and materials sold separately.
              Final quote confirmed after a consultation.
            </p>
            <Link to="/book?promo=home-theater" data-testid="book-home-theater-btn" className={`${btnGold} mt-6`}>
              Book Consultation — $0 Today
            </Link>
          </section>

          <section id="media-wall" data-testid="promo-media-wall"
            className="rounded-2xl bg-[#121216] gold-border p-6 sm:p-8 scroll-mt-24 flex flex-col">
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Starting at</p>
            <p className="font-display text-5xl text-[#d4af37]">$799</p>
            <h2 className="font-display text-xl uppercase text-white mt-2 leading-tight">Luxury Modular Media Wall Installation</h2>
            <ul className="mt-5 space-y-2 flex-1">
              {["Modular entertainment wall installation", "Professional TV mounting",
                "Decorative panels and shelving", "LED lighting integration",
                "Optional electric fireplace integration"].map((i) => (
                <li key={i} className="text-[12px] text-zinc-300 leading-relaxed flex gap-2">
                  <span className="text-[#d4af37] mt-0.5">•</span>{i}
                </li>
              ))}
            </ul>
            <p className="text-[10px] font-mono text-zinc-500 leading-relaxed mt-4 border-t border-zinc-800 pt-4">
              Starting price is for qualifying installation labor; materials and equipment are separate.
              Final quote confirmed after a consultation.
            </p>
            <Link to="/book?promo=media-wall" data-testid="book-media-wall-btn" className={`${btnGold} mt-6`}>
              Book Consultation — $0 Today
            </Link>
          </section>
        </div>

        {/* Areas We Serve */}
        <section className="border-t border-[#d4af37]/15" data-testid="promo-areas">
          <div className="max-w-3xl mx-auto px-6 py-12 text-center">
            <h2 className="font-display text-2xl uppercase text-white">Areas We Serve</h2>
            <p className="text-sm text-zinc-400 leading-relaxed mt-4">{AREAS_INTRO}</p>
            <p className="text-xs text-zinc-300 font-mono mt-4">
              <span className="text-[#d4af37] font-bold">Primary: Irving, TX — including Las Colinas</span>
            </p>
            <p className="text-[11px] font-mono text-zinc-500 leading-relaxed mt-2">
              Also serving {AREAS_OTHER.join(", ")} and surrounding DFW communities.
            </p>
            <p className="text-[11px] font-mono text-zinc-500 mt-6">
              Looking for TV mounting specifically? See our <Link to="/dfw-tv-mounting" className="text-[#d4af37] hover:underline">Dallas–Fort Worth TV mounting page</Link> or
              the <Link to="/" className="text-[#d4af37] hover:underline">DuroTech28 homepage</Link>.
            </p>
          </div>
        </section>

        {/* Combine note */}
        <div className="max-w-3xl mx-auto px-6 pb-10 text-center">
          <p className="text-[11px] font-mono text-zinc-400 leading-relaxed" data-testid="promo-combine-note">
            Want to combine a promotion with other DuroTech28 services — security cameras, smart locks, Wi-Fi or cabling?
            Add them when booking and we'll quote the extras on site, or browse the full catalog.
          </p>
          <Link to="/services" data-testid="promo-combine-link"
            className="inline-block mt-3 text-[11px] font-mono uppercase tracking-widest text-[#d4af37] hover:underline">
            Combine With Other Services →
          </Link>
        </div>

        <section className="border-t border-[#d4af37]/15">
          <div className="max-w-3xl mx-auto px-6 py-12 text-center">
            <h2 className="font-display text-2xl uppercase text-white">Pay Now or Pay After Installation</h2>
            <p className="text-xs text-zinc-400 leading-relaxed mt-3 max-w-xl mx-auto">
              Every promotion gives you both options at checkout: pay the advertised price securely online,
              or reserve your appointment with $0 upfront and pay after the installation is completed.
              Either way, your 3-hour appointment window is reserved immediately.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <a href={PHONE_TEL} data-testid="promo-bottom-call" className={btnCall}>Call {PHONE_DISPLAY}</a>
              <Link to="/services" data-testid="promo-bottom-services" className={btnGold}>All Services →</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
